import type { Partner } from "@expert/shared-types";
import type { SplitterPaneSize } from "@mantine/hooks";

import { getLogger } from "@expert/logging";
import { Box, Splitter } from "@mantine/core";
import { type JSX, type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { UI_ORCHESTRATION_COMPONENT } from "@/expert-hub/data/expertHubErrorMetric";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { trackedBoundaryProps } from "@/shared-utils";

import type { C360HistoryInteraction } from "../api/interactions";

import { c360Dispatcher } from "../c360Analytics";
import { CustomerHistoryPanel, useCustomerHistoryView } from "./CustomerHistoryPanel";

const panelLogger = getLogger({ module: "CustomerHistoryPanel" });

/** The panel's header bar, which is all of it until there's history to show. Matches `.header` in CustomerHistoryPanel.module.css. */
const CUSTOMER_HISTORY_HEADER_PX = 40;
const CUSTOMER_HISTORY_HEADER_HEIGHT = `${CUSTOMER_HISTORY_HEADER_PX}px`;
const TRANSCRIPT_MIN = "160px";
// History first, above the transcript.
const OPEN_SIZES: SplitterPaneSize[] = ["35%", "65%"];

/**
 * The live transcript with the call's Customer 360 history above it, when the call's lookup asked for it
 * (`isC360CustomerHistoryEnabled`). Otherwise, or before the lookup starts, the history takes no space. While the lookup runs, and when it finds
 * nothing, the history is only its header; when it finds earlier interactions it opens. The expert drags the
 * line between them to resize, and collapses the history to its header with its toggle or by dragging it there.
 */
export function TranscriptWithCustomerHistory({
    sessionId,
    transcript,
}: {
    sessionId: string;
    transcript: ReactNode;
}): JSX.Element {
    const partner = useSessionStore((state) => state.getSession(sessionId)?.currentTask?.partner);
    const view = useCustomerHistoryView(sessionId);
    const boxRef = useRef<HTMLDivElement>(null);
    const [openSizes, setOpenSizes] = useState<SplitterPaneSize[]>(OPEN_SIZES);
    const [collapsedByExpert, setCollapsedByExpert] = useState(false);
    const hasResults = view.kind === "results";
    // Mantine's drag keeps calling the onSizeChange from the render that attached it, so a panel first shown without
    // history would ignore every drag; the handler reads whether there's history from this ref instead.
    const hasResultsRef = useRef(hasResults);
    useLayoutEffect(() => {
        hasResultsRef.current = hasResults;
    });

    // Opens on its own when history arrives, unless the expert closed it.
    const expanded = hasResults && !collapsedByExpert;
    // Hidden, the history takes no space; until there's history it's only its header. The splitter stays mounted
    // either way, so the live transcript isn't remounted when the lookup starts.
    const closedSize = view.kind === "hidden" ? "0px" : CUSTOMER_HISTORY_HEADER_HEIGHT;
    const sizes: SplitterPaneSize[] = expanded ? openSizes : [closedSize, "100%"];

    return (
        <Box ref={boxRef} h="100%" w="100%">
            <Splitter
                orientation="vertical"
                h="100%"
                w="100%"
                lineSize={view.kind === "hidden" ? 0 : 1}
                // Only history can be dragged, so without it there's no grip to suggest it can.
                withHandle={hasResults}
                handleColor="dark.5"
                resetOnDoubleClick={false}
                sizes={sizes}
                onSizeChange={(next) => {
                    if (!hasResultsRef.current) return;
                    // Dragged down to its header counts as closing it; Show reopens it at the size it had.
                    if (historyPanePx(next[0], boxRef.current) <= CUSTOMER_HISTORY_HEADER_PX + 1) {
                        setCollapsedByExpert(true);
                        return;
                    }
                    setOpenSizes(next);
                    // Dragging it open again counts as expanding it.
                    setCollapsedByExpert(false);
                }}
            >
                {/* `defaultSize` is required by the pane type but ignored while `sizes` is controlled. */}
                <Splitter.Pane
                    defaultSize={OPEN_SIZES[0]!}
                    // Only history can be dragged open; without it the panel stays its header.
                    min={hasResults ? CUSTOMER_HISTORY_HEADER_HEIGHT : closedSize}
                    max={hasResults ? undefined : closedSize}
                >
                    {view.kind !== "hidden" && (
                        // A panel that fails to render takes only itself down, not the live transcript beside it.
                        <ErrorBoundary {...trackedBoundaryProps(panelLogger, UI_ORCHESTRATION_COMPONENT)}>
                            <CustomerHistoryPanel
                                view={view}
                                expanded={expanded}
                                onToggle={() => setCollapsedByExpert(expanded)}
                                onSummaryExpanded={(position, interaction) =>
                                    reportSummaryExpanded(sessionId, partner, position, interaction)
                                }
                            />
                        </ErrorBoundary>
                    )}
                </Splitter.Pane>
                <Splitter.Pane defaultSize={OPEN_SIZES[1]!} min={TRANSCRIPT_MIN}>
                    <Box pos="relative" h="100%">
                        {transcript}
                    </Box>
                </Splitter.Pane>
            </Splitter>
        </Box>
    );
}

/** The history pane's height in pixels; the splitter reports it in whichever unit the size was set in. */
function historyPanePx(size: SplitterPaneSize | undefined, box: HTMLElement | null): number {
    if (size === undefined) return Number.POSITIVE_INFINITY;
    const value = Number.parseFloat(String(size));
    // Without the box a percentage can't be measured, so it's treated as unknown rather than collapsed.
    if (String(size).endsWith("%")) {
        return box ? (value / 100) * box.getBoundingClientRect().height : Number.POSITIVE_INFINITY;
    }
    return value;
}

/** An opened summary, for analytics (EDP): which row and what kind of interaction, never the summary itself. */
function reportSummaryExpanded(
    sessionId: string,
    partner: Partner | undefined,
    position: number,
    { channel, status }: C360HistoryInteraction,
): void {
    if (!partner) return;
    void c360Dispatcher(sessionId, partner).dispatchBusinessEvent("C360HistorySummaryExpanded", {
        position,
        ...(channel ? { interactionChannel: channel } : {}),
        ...(status ? { interactionStatus: status } : {}),
    });
}
